import React, { useMemo, useContext } from 'react';
import { useProject } from '../../store/projectContext';
import { TimelineContext } from '../timeline-position-state';
import { Sequence } from '../Sequence';
import { Audio } from '../audio/Audio';

const ASSET_BASE = 'https://dash.animaker.com/a/u/';

function resolveUrl(src) {
  if (!src) return '';
  if (src === 'src/sample/sample.mp4') return '/sample.mp4';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${ASSET_BASE}${src}`;
}

/**
 * Collects all audio clips from the flattened scene structure
 * and renders them as Remotion Audio sequences.
 * 
 * Audio sources:
 * 1. project.bgm — background music track
 * 2. scene.audios[] — per-scene audio (TTS/lip-sync clips)
 */
export function AudioSequences() {
  const project = useProject();
  const { muted } = useContext(TimelineContext);
  const fps = project?.fps || 30;
  const mutedAudios = project?.mutedAudios || {};

  const audioEntries = useMemo(() => {
    const entries = [];

    // Background music from project.bgm
    if (project?.bgm && project.bgm.src) {
      const bgm = project.bgm;
      const playStart = bgm.playStart || 0;
      const playEnd = bgm.playEnd || playStart;
      const fromFrame = Math.max(0, Math.round(playStart * fps));
      const durationInFrames = Math.max(1, Math.round((playEnd - playStart) * fps));
      const volume = bgm.volume != null ? bgm.volume : 0.1;
      const src = resolveUrl(bgm.src);
      if (src) {
        entries.push({ id: 'bgm', src, fromFrame: 0, durationInFrames, startFrom: fromFrame, volume, track: 'track1' });
      }
    }

    // Per-scene audio clips
    if (project?.scenes) {
      const seen = new Set();

      for (const scene of project.scenes) {
        for (const audio of (scene.audios || [])) {
          if (!audio || !audio.src || !audio.id) continue;
          if (seen.has(audio.id)) continue;
          seen.add(audio.id);

          const playStart = audio.playStart || 0;
          const playEnd = audio.playEnd || playStart;
          const fromFrame = Math.max(0, Math.round(playStart * fps));
          const durationInFrames = Math.max(1, Math.round((playEnd - playStart) * fps));
          const volume = audio.volume != null ? audio.volume : 1;
          const src = resolveUrl(audio.src);

          if (src) {
            entries.push({
              id: audio.id,
              src,
              fromFrame,
              durationInFrames,
              startFrom: 0,
              volume,
              track: 'track0',
              isLipSync: audio.isLipSync || false,
            });
          }
        }
      }
    }

    return entries;
  }, [project?.bgm, project?.scenes, fps]);

  return (
    <>
      {audioEntries.map(({ id, src, fromFrame, durationInFrames, startFrom, volume }) => (
        <Sequence
          key={id}
          from={fromFrame}
          durationInFrames={durationInFrames}
          layout="none"
          showInTimeline={false}
        >
          <Audio
            src={src}
            startFrom={startFrom}
            volume={muted || mutedAudios[id] ? 0 : volume}
            muted={muted || !!mutedAudios[id]}
          />
        </Sequence>
      ))}
    </>
  );
}
